import { useEffect, useMemo, useState } from 'react';
import '../../assets/css/case-property.css';
// @ts-ignore
import { useProperty, EditableToken, useContentToken, useSlotGroup, EditableSlotGroup, useCollectionBinding, EditableCollectionBinding } from '@vibesites/runtime/react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
// @ts-ignore
import { Dialog, DialogContent, DialogTitle, VibesitesImage } from '@vibesites/ui-primitives';
import type { PropertySpecialOffer, RawSpecialOffer } from './propertyUtils';
import { normalizeSpecialOffers } from './propertyUtils';
import { useAutoRotatingIndex } from './useAutoRotatingIndex';

const AUTO_SHOW_DELAY_MS = 1200;
const STORAGE_KEY_PREFIX = 'case-special-offer-dismissed:';
export const SPECIAL_OFFER_EVENT = 'open-special-offer';

/* ── Image slot group definition ──────────────────────────────────────── */

const IMAGE_SLOT_DEF: SlotGroupDefinition = {
  label: 'Popup Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Special offer image',
    },
  },
};

export function openSpecialOfferPopup(index?: number) {
  window.dispatchEvent(new CustomEvent(SPECIAL_OFFER_EVENT, { detail: { index } }));
}

function buildOfferKey(propertySlug: string, offerTitle: string, offerDescription: string, offerDisclaimer: string): string {
  return `${propertySlug}::${offerTitle}::${offerDescription}::${offerDisclaimer}`;
}

function buildOfferCollectionKey(propertySlug: string, offers: PropertySpecialOffer[]): string {
  if (!propertySlug || offers.length === 0) return '';
  return offers
    .map(offer => buildOfferKey(propertySlug, offer.title, offer.description, offer.disclaimer))
    .join('||');
}

export function SpecialOffers() {
  const property = useProperty() as Record<string, any>;
  // Live special offers via collection binding so edits/new offers show up
  // without a redeploy; `property` is still used for the dismissal-key slug.
  const rawOffers = useCollectionBinding<RawSpecialOffer>('specialoffers.items', {
    collection: 'special_offers',
  });
  const offers = useMemo(() => normalizeSpecialOffers(rawOffers), [rawOffers]);
  const badgeText = useContentToken('specialoffers.badge_text') || contentSchema.tokens['specialoffers.badge_text'].defaultValue;
  const [open, setOpen] = useState(false);
  const { currentIndex, setCurrentIndex, hasMultipleItems } = useAutoRotatingIndex(offers.length, open);

  /* ── Image slot group & visibility toggle ─────────────────────────── */
  const imageSlot = useSlotGroup('specialoffers.popup_image', IMAGE_SLOT_DEF);
  const showImageValue = useContentToken('specialoffers.show_image') || contentSchema.tokens['specialoffers.show_image'].defaultValue;
  const showImage = showImageValue === 'true';

  const propertySlug = property?._slug || property?.property_slug || '';
  const offerKey = useMemo(() => {
    return buildOfferCollectionKey(propertySlug, offers);
  }, [offers, propertySlug]);

  useEffect(() => {
    setCurrentIndex(0);
  }, [offerKey, setCurrentIndex]);

  useEffect(() => {
    if (offers.length === 0 || !offerKey || typeof window === 'undefined') {
      setOpen(false);
      return;
    }

    const storageKey = `${STORAGE_KEY_PREFIX}${offerKey}`;
    const dismissed = window.sessionStorage.getItem(storageKey) === 'true';
    if (dismissed) return;

    const timer = window.setTimeout(() => {
      setOpen(true);
    }, AUTO_SHOW_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [offerKey, offers.length]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleOpen = (event: Event) => {
      if (offers.length === 0) return;
      const requestedIndex = (event as CustomEvent<{ index?: number }>).detail?.index;
      if (typeof requestedIndex === 'number' && requestedIndex >= 0 && requestedIndex < offers.length) {
        setCurrentIndex(requestedIndex);
      }
      setOpen(true);
    };
    window.addEventListener(SPECIAL_OFFER_EVENT, handleOpen);
    return () => window.removeEventListener(SPECIAL_OFFER_EVENT, handleOpen);
  }, [offers.length, setCurrentIndex]);

  const activeOffer = offers[currentIndex] || offers[0];
  if (!activeOffer || !offerKey) return null;

  const headline = activeOffer.title || 'Special Offer';

  const handleClose = () => {
    setOpen(false);
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(`${STORAGE_KEY_PREFIX}${offerKey}`, 'true');
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          handleClose();
          return;
        }
        setOpen(true);
      }}>
        <DialogContent
          className={`prop-offer-dialog-content p-0 gap-0 border-0 overflow-hidden ${showImage ? 'sm:max-w-[960px]' : 'sm:max-w-[860px]'}`}
          style={{ borderRadius: '1rem' }}
        >
          <DialogTitle className="sr-only" style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', borderWidth: 0 }}>
            {headline}
          </DialogTitle>
          <div className={`prop-offer-dialog ${showImage ? 'prop-offer-dialog--with-image' : ''}`}>
            {/* ── Hidden editable token for show_image toggle ──── */}
            <EditableToken
              path="specialoffers.show_image"
              type="text"
              label="Show Image (true / false)"
              as="span"
              style={{ display: 'none' }}
              defaultValue={contentSchema.tokens['specialoffers.show_image'].defaultValue}
            >
              {showImageValue}
            </EditableToken>
            {/* ── Left image panel (only when show_image is "true") ──── */}
            {showImage && imageSlot.image && (
              <EditableSlotGroup groupPath="specialoffers.popup_image" definition={IMAGE_SLOT_DEF} className="prop-offer-dialog-image-wrapper">
                <div className="prop-offer-dialog-image">
                  <VibesitesImage
                    url={imageSlot.image}
                    alt={imageSlot.alt || 'Special offer image'}
                    sizes="(min-width: 768px) 400px, 100vw"
                  />
                </div>
              </EditableSlotGroup>
            )}

            {/* ── Offer content carousel ──────────────────────────── */}
            <EditableCollectionBinding
              bindingPath="specialoffers.items"
              label="Special Offers"
              className="prop-offer-carousel prop-offer-carousel--dialog"
            >
              <div className="prop-offer-carousel-viewport">
                <div
                  className="prop-offer-carousel-track"
                  style={{ transform: `translateX(-${currentIndex * 100}%)` }}
                >
                  {offers.map((offer, index) => (
                    <div
                      key={`${offer.title}-${offer.description}-${offer.disclaimer}-${index}`}
                      className="prop-offer-carousel-slide prop-offer-dialog-slide"
                    >
                      <div className="prop-offer-dialog-copy">
                        <EditableToken
                          path="specialoffers.badge_text"
                          type="text"
                          label="Popup Badge Text"
                          as="span"
                          className="prop-offer-dialog-badge"
                          defaultValue={contentSchema.tokens['specialoffers.badge_text'].defaultValue}
                        >
                          {badgeText}
                        </EditableToken>
                        <h2 className="prop-offer-dialog-title">{offer.title || 'Special Offer'}</h2>
                        {offer.description && <p className="prop-offer-dialog-body">{offer.description}</p>}
                        {offer.disclaimer && <p className="prop-offer-dialog-disclaimer">{offer.disclaimer}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {hasMultipleItems && (
                <div className="prop-offer-carousel-pagination prop-offer-carousel-pagination--dialog">
                  {offers.map((offer, index) => (
                    <button
                      key={`${offer.title}-${index}`}
                      type="button"
                      className={`prop-offer-carousel-dot${index === currentIndex ? ' is-active' : ''}`}
                      onClick={() => setCurrentIndex(index)}
                      aria-label={`Show offer ${index + 1}`}
                      aria-current={index === currentIndex ? 'true' : undefined}
                    />
                  ))}
                </div>
              )}
            </EditableCollectionBinding>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SpecialOffers',
  displayName: 'Special Offers Popup',
  tokens: {
    'specialoffers.badge_text': {
      label: 'Popup Badge Text',
      type: 'text',
      defaultValue: 'Limited Time Offer',
    },
    'specialoffers.show_image': {
      label: 'Show Image (true / false)',
      type: 'text',
      defaultValue: 'false',
    },
  },
  slotGroups: {
    'specialoffers.popup_image': IMAGE_SLOT_DEF,
  },
  collectionBindings: {
    'specialoffers.items': {
      label: 'Special Offers',
      collection: 'special_offers',
    },
  },
};

(SpecialOffers as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default SpecialOffers;
